import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Copy, Share2, MessageCircle, Mail, ArrowRight, Twitter } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Confetti() {
  // Pure CSS confetti strip — dots streak down briefly, no library
  const pieces = Array.from({ length: 50 });
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-10" aria-hidden="true">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const dur = 1.8 + Math.random() * 1.6;
        const size = 6 + Math.random() * 8;
        const hue = Math.random() > 0.5 ? "#D4AF37" : "#F5E5A7";
        return (
          <span
            key={i}
            className="absolute top-[-20px]"
            style={{
              left: `${left}%`,
              width: size,
              height: size,
              background: hue,
              opacity: 0.9,
              borderRadius: Math.random() > 0.5 ? "50%" : "2px",
              animation: `confetti-fall ${dur}s ease-in ${delay}s forwards`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Welcome() {
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(false);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      // No session — user probably navigated here directly. Check if they're paid already.
      (async () => {
        try {
          const { data } = await api.get("/profile");
          if (data.profile) setProfile(data.profile);
          setPaid(Boolean(user?.plan));
        } catch {}
        setLoading(false);
      })();
      return;
    }

    // Poll payment status up to ~20 seconds
    let attempts = 0;
    const checkPayment = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaid(true);
          // Meta Pixel — Purchase (fire once)
          if (typeof window !== "undefined" && window.fbq && !window.__esn_purchase_fired) {
            window.__esn_purchase_fired = true;
            window.fbq("track", "Purchase", {
              content_name: "Elite Scouting Network — Profile Access",
              content_category: "football_profile",
              value: 19.00,
              currency: "USD",
            });
          }
          if (refreshUser) await refreshUser();
          try {
            const { data: pdata } = await api.get("/profile");
            if (pdata.profile) setProfile(pdata.profile);
          } catch {}
          setLoading(false);
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
      } catch {}
      if (attempts >= 10) {
        setLoading(false);
        if (pollRef.current) clearInterval(pollRef.current);
      }
    };
    checkPayment();
    pollRef.current = setInterval(checkPayment, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [searchParams, refreshUser, user]);

  const slug = profile?.slug || "";
  const publicUrl = slug ? `${window.location.origin}/player/${slug}` : "";
  const shareMsg = `I'm now inside the Elite Scouting Network. Here's my profile — take a look: ${publicUrl}`;

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg)}`, "_blank");
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMsg)}`, "_blank");
  const shareEmail = () => {
    const subject = encodeURIComponent("My player profile — Elite Scouting Network");
    const body = encodeURIComponent(shareMsg);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const qrUrl = publicUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(publicUrl)}&bgcolor=0A0A0A&color=D4AF37&margin=10`
    : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="welcome-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#A3A3A3] text-sm tracking-wide">Confirming your payment…</p>
        </div>
      </div>
    );
  }

  // Fallback: couldn't confirm payment — push to paywall
  if (!paid) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="welcome-unpaid">
        <div className="max-w-md text-center">
          <p className="text-[#A3A3A3] text-sm mb-4">We couldn't confirm your payment just yet.</p>
          <button
            onClick={() => navigate("/paywall")}
            className="btn-gold px-8 py-3"
            data-testid="welcome-retry"
          >
            Return to payment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative" data-testid="welcome-page">
      <Confetti />

      <div className="relative z-20 max-w-2xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20 pb-24">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#181207] mb-6">
            <Check size={12} className="text-[#D4AF37]" />
            <span className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.22em]">Payment confirmed</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-[#F5F5F5] leading-[0.95] mb-4" data-testid="welcome-headline">
            You're <span className="text-[#D4AF37]">inside.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#A3A3A3] max-w-lg mx-auto leading-relaxed">
            Your profile is live inside the network. Here's the link you can send anywhere.
          </p>
        </div>

        {/* Share card */}
        {publicUrl && (
          <div className="bg-[#0E0E0E] border border-[#1A1A1A] rounded-md p-6 sm:p-8 mb-10" data-testid="welcome-share-card">
            <p className="text-[10px] text-[#D4AF37] tracking-[0.3em] uppercase mb-4">Your shareable profile link</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {qrUrl && (
                <img
                  src={qrUrl}
                  alt="QR code to your profile"
                  className="w-28 h-28 rounded-sm border border-[#1A1A1A] flex-shrink-0"
                  data-testid="welcome-qr"
                />
              )}
              <div className="min-w-0 flex-1 w-full">
                <div className="bg-[#141414] border border-[#262626] p-3 rounded-sm mb-3 break-all" data-testid="welcome-link">
                  <p className="text-[12px] sm:text-[13px] text-[#E5E5E5] font-mono leading-relaxed">{publicUrl}</p>
                </div>
                <button
                  onClick={copyLink}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-all ${
                    copied
                      ? "bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]"
                      : "bg-[#D4AF37] text-black hover:bg-[#E5C04A]"
                  }`}
                  data-testid="welcome-copy"
                >
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Link</>}
                </button>
              </div>
            </div>

            {/* Share targets */}
            <div className="grid grid-cols-3 gap-2 mt-5 pt-5 border-t border-[#1A1A1A]">
              <button
                onClick={shareWhatsApp}
                className="flex flex-col items-center justify-center gap-1.5 py-3 bg-[#141414] border border-[#262626] hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all rounded-sm"
                data-testid="welcome-share-whatsapp"
              >
                <MessageCircle size={16} />
                <span className="text-[10px] font-bold uppercase tracking-wider">WhatsApp</span>
              </button>
              <button
                onClick={shareTwitter}
                className="flex flex-col items-center justify-center gap-1.5 py-3 bg-[#141414] border border-[#262626] hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all rounded-sm"
                data-testid="welcome-share-twitter"
              >
                <Twitter size={16} />
                <span className="text-[10px] font-bold uppercase tracking-wider">X / Twitter</span>
              </button>
              <button
                onClick={shareEmail}
                className="flex flex-col items-center justify-center gap-1.5 py-3 bg-[#141414] border border-[#262626] hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all rounded-sm"
                data-testid="welcome-share-email"
              >
                <Mail size={16} />
                <span className="text-[10px] font-bold uppercase tracking-wider">Email</span>
              </button>
            </div>
          </div>
        )}

        {/* Next actions */}
        <div className="bg-[#0c0c0c] border border-[#1A1A1A] rounded-md p-6 sm:p-8 mb-10" data-testid="welcome-next-steps">
          <p className="text-[10px] text-[#D4AF37] tracking-[0.3em] uppercase mb-4">Your next move</p>
          <p className="text-lg sm:text-xl text-[#F5F5F5] font-semibold leading-snug mb-2">
            Send it to 3 people this week.
          </p>
          <p className="text-[13px] sm:text-[14px] text-[#9a9a9a] leading-relaxed">
            A coach. A scout. A club contact. The players who get taken seriously are the ones
            who put their profile in front of the right eyes — quickly, confidently, properly positioned.
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3">
          {slug && (
            <Link
              to={`/player/${slug}`}
              className="flex-1 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-widest uppercase text-sm px-6 py-4 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2"
              data-testid="welcome-view-profile"
            >
              <Share2 size={16} /> View My Public Profile
            </Link>
          )}
          <Link
            to="/profile/edit"
            className="flex-1 bg-transparent border border-[#262626] text-[#E5E5E5] font-bold tracking-widest uppercase text-sm px-6 py-4 rounded-full hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all inline-flex items-center justify-center gap-2"
            data-testid="welcome-edit-profile"
          >
            Edit My Profile <ArrowRight size={16} />
          </Link>
        </div>

        <p className="text-center text-[11px] text-[#555] mt-12 tracking-wide">
          A receipt has been sent to your email · One-off payment · 14-day refund
        </p>
      </div>
    </div>
  );
}
